import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { User as UserIcon, ChevronDown, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '../../../stores/authStore';
import type { MembershipTier } from '../../../types/auth';
import { creditsApi } from '../../../services/creditsApi';
import type { PlanInfo } from '../../../services/creditsApi';

/**
 * 订阅会员弹窗 —— 按 Figma 158-25「开通会员」整页设计稿 1:1 还原（1920 宽 / #121212 底 / 内容列 1366 居中）
 *
 * 结构（自上而下）：
 *   顶部栏 sticky（返回按钮 + 用户信息卡 + 购买积分/申请发票/订阅与发票）
 *   Banner 1366x136（渐变描边 + 背景图 + 内测加赠文案 + 天/时/分/秒倒计时 4 卡）
 *   渐变主标题「订阅 Bong 会员，让创作更快一步」+ 副标题
 *   个人创作/团队协作切换器（设计稿 158-471；团队态暂无设计稿，内容区显示占位提示）
 *   会员卡 x4（月度/季度/年卡/年卡PRO，312 宽外卡 + 36 顶部条 + 568 高内卡）
 *   常见问题 x6（问题/答案手风琴）
 *
 * 会员规则：未订阅=普通用户（none）/ 月度=月卡（monthly）/ 季度=季卡（quarterly）/
 * 年卡（yearly）/ 年卡PRO（yearlyPro），从 user.membershipTier 解析，后端接入后自动生效。
 * 套餐表驱动（P1）：挂载即拉 GET /api/plans，卡片文案按公式生成；
 * 失败或定价表留空（灰度）时回落设计稿本地 PLANS。
 * Banner 运营化（P2）：挂载即拉 GET /api/banners?position=membership（运维平台「运营管理-轮播图」
 * 位置=会员页为单一数据源），标题/副标题/背景图/倒计时截止（结束时间）由接口覆盖；
 * 失败或无数据时回落设计稿默认文案（DEFAULT_BANNER）。
 *
 * 层级：z-[250]，盖过顶栏（200）与顶栏下拉（150），低于登录弹窗（300）。
 * 渲染：createPortal 到 body，独立于顶栏层叠上下文；ESC 关闭 + body 滚动锁定。
 */

/** 内测倒计时截止时间（内测期 +30 天，每天前 3 个加赠档位文案见 Banner）；接口无结束时间时的兜底 */
const BETA_END_AT = new Date('2026-11-14T00:00:00+08:00').getTime();

/** 会员页 Banner 兜底数据（设计稿 164-40 文案）；接口 GET /api/banners?position=membership 成功时逐字段覆盖 */
const DEFAULT_BANNER = {
  title: '内测期间订阅会员加赠积分，创作更划算！',
  description: '1K图可多做50张，2K图可多做16张，4K图可多做5张',
  /** 背景图：空串回落内置切图 banner-bg.png */
  cover: '',
  endAt: BETA_END_AT,
};
type PromoBannerData = typeof DEFAULT_BANNER;

/** 会员档位展示名（顶栏当前订阅状态）：none=普通用户 / monthly=月卡会员 / quarterly=季卡会员 */
const TIER_LABEL: Record<MembershipTier, string> = {
  none: '普通用户',
  monthly: '月卡会员',
  quarterly: '季卡会员',
  yearly: '年卡会员',
  yearlyPro: '年卡Pro会员',
};

/** 解析会员档位：后端字段缺失时按未订阅（普通用户）处理 */
const resolveTier = (tier?: MembershipTier): MembershipTier => tier ?? 'none';

/** 顶部栏右侧通用按钮：设计稿 80~94x40 / #222222 / 1px #363636 / 圆角 12 / 14px Medium 白，文字居中；
    muted：通道未开放置灰（40% 透明 + cursor-not-allowed，去 hover 升亮），保留点击 toast 反馈 */
const TopBarAction: React.FC<{ label: string; width: number; className?: string; onClick?: () => void; muted?: boolean }> = ({ label, width, className, onClick, muted }) => (
  <button
    onClick={onClick}
    className={`h-10 rounded-xl text-sm font-medium text-white transition-colors ${muted ? 'cursor-not-allowed opacity-40' : 'hover:bg-[#2D2D2D]'} ${className ?? ''}`}
    style={{ width, backgroundColor: '#222222', border: '1px solid #363636' }}
  >
    {label}
  </button>
);

/** 倒计时：每秒刷新，返回距截止时刻 endAt 的 天/时/分/秒（补零两位，天不补） */
const useCountdown = (endAt: number): { d: string; h: string; m: string; s: string } => {
  const calc = () => {
    const diff = Math.max(0, endAt - Date.now());
    const sec = Math.floor(diff / 1000);
    return {
      d: String(Math.floor(sec / 86400)),
      h: String(Math.floor((sec % 86400) / 3600)).padStart(2, '0'),
      m: String(Math.floor((sec % 3600) / 60)).padStart(2, '0'),
      s: String(sec % 60).padStart(2, '0'),
    };
  };
  const [t, setT] = useState(calc);
  useEffect(() => {
    setT(calc());
    const timer = setInterval(() => setT(calc()), 1000);
    return () => clearInterval(timer);
  }, [endAt]);
  return t;
};

/** 倒计时单元卡：设计稿 80x96（sm+）；手机缩为 64x80；数字 40px 快看世界体（仅 Regular 字重，视觉即粗体，手机 30px），单位 16px Semibold */
const CountdownUnit: React.FC<{ value: string; unit: string }> = ({ value, unit }) => (
  <div
    className="flex h-20 w-16 flex-col items-center rounded-xl sm:h-24 sm:w-20"
    style={{ backgroundColor: 'rgba(0,0,0,0.05)', border: '1px solid #FFFFFF' }}
  >
    {/* 数字：CKTKingkong Bold 40px/40px 白居中（设计稿 164-40 style_08a832e8）；数字非等宽，不加 tabular-nums */}
    <span
      className="mt-3 flex h-10 items-center text-3xl font-normal leading-none text-white sm:text-[40px]"
      style={{ fontFamily: "'CKTKingkong', 'PingFang SC', 'Microsoft YaHei', sans-serif" }}
    >
      {value}
    </span>
    <span className="mt-4 text-base font-semibold leading-none text-white">{unit}</span>
  </div>
);

/** 顶部栏：设计稿 251-40（1920x112）——返回按钮 (24,24) 贴视口左缘（切图自带 #2D2D2D 圆角底）；
    用户信息 (277,24) 贴 1366 内容列左缘（头像 64 + 16 间距 + 文本列：昵称行 y=12 / 信息行 y=42）；
    右侧按钮组 y=36 右缘贴内容列（购买积分 80 / 申请发票 80 / 订阅与发票 94，间距 10/8）。
    流式适配：内容列以 1366 为上限、窄屏收缩（px-6，视口 ≥1440 时零边距 1:1 设计稿）；
    窄屏用户信息右移 88px 避让返回按钮（24+36+28）；手机（<sm）隐藏右侧按钮组防破版；
    设计稿背景矩形 opacity=0（透明），实现保留 #121212 实底以支撑 sticky 滚动遮盖（与整页同色，视觉无差） */
const ModalTopBar: React.FC<{ tier: MembershipTier; onBack: () => void; onOpenRedeem: () => void }> = ({ tier, onBack, onOpenRedeem }) => {
  const { user } = useAuthStore();
  return (
    <div className="sticky top-0 z-20" style={{ backgroundColor: '#121212' }}>
      {/* z-20 须高于横幅内文案/倒计时卡的 relative z-10，滚动时才能完整遮盖横幅内容 */}
      {/* 返回按钮：视口左缘 (24,24)，36x36 切图直接渲染 */}
      <button
        onClick={onBack}
        aria-label="返回"
        className="absolute left-6 top-6 transition-opacity hover:opacity-80"
      >
        <img src="/icons/membership/back-btn.svg" alt="返回" width={36} height={36} />
      </button>
      <div className="relative mx-auto h-28 w-full max-w-[1366px] px-6 min-[1440px]:px-0">
        {/* 用户信息：头像 64 圆形 + 文本列（相对头像顶 12px）；窄屏左移位避让返回按钮 */}
        <div className="absolute left-[88px] top-6 flex items-start gap-4 min-[1440px]:left-0">
          {user?.avatar ? (
            <img src={user.avatar} alt={user.nickname} className="h-16 w-16 shrink-0 rounded-full object-cover" />
          ) : (
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full text-white" style={{ backgroundColor: '#0092FF' }}>
              <UserIcon size={24} />
            </div>
          )}
          <div className="mt-3 flex flex-col">
            {/* 昵称行：18px Medium #8BCDFF + 编辑 icon 16（间距 8，65% 透明） */}
            <div className="flex items-center">
              <span className="text-lg font-medium leading-none" style={{ color: '#8BCDFF' }}>{user?.nickname ?? '用户'}</span>
              <img src="/icons/user-center/edit.svg" alt="编辑" width={16} height={16} className="ml-2 opacity-65" />
            </div>
            {/* 信息行：token 12 + 积分 14px Medium 白 + 分隔线 1x8 #D9D9D9 45% + 档位 14px Regular 45% 白 */}
            <div className="mt-3 flex items-center">
              <img src="/icons/user-center/token-icon.svg" alt="" width={12} height={12} />
              <span className="ml-1 text-sm font-medium leading-none text-white">{(user?.points ?? 0) + (user?.dailyPoints ?? 0)}</span>
              <span className="ml-3 h-2 w-px" style={{ backgroundColor: 'rgba(217,217,217,0.45)' }} />
              <span className="ml-3 text-sm leading-none" style={{ color: 'rgba(255,255,255,0.45)' }}>{TIER_LABEL[tier]}</span>
            </div>
          </div>
        </div>
        {/* 右侧按钮组：y=36，间距 10/8，右缘贴内容列；手机隐藏防破版；
            兑换码（P2 运维平台「兑换码」核销）置于组首 */}
        <div className="absolute right-6 top-9 hidden items-center sm:flex min-[1440px]:right-0">
          <TopBarAction label="兑换码" width={80} onClick={onOpenRedeem} />
          {/* 购买/发票通道未开放（P3 待支付接入）：置灰保留点击 toast 反馈 */}
          <TopBarAction muted label="购买积分" width={80} className="ml-[10px]" onClick={() => toast('购买通道即将开放，敬请期待')} />
          <TopBarAction muted label="申请发票" width={80} className="ml-2" onClick={() => toast('发票功能即将开放，敬请期待')} />
          <TopBarAction muted label="订阅与发票" width={94} className="ml-2" onClick={() => toast('发票功能即将开放，敬请期待')} />
        </div>
      </div>
    </div>
  );
};

/** 兑换码弹窗（P2 运维平台「兑换码」核销）：credits 型直接入账、plan 型开通订阅；
    成功后 fetchUser 刷新积分，顶栏信息行与用户菜单积分即时同步。
    层级 z-[260]：盖过会员整页（250），ESC/点击遮罩关闭 */
const RedeemDialog: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<{ ok: boolean; text: string } | null>(null);
  const { fetchUser } = useAuthStore();

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const submit = async () => {
    const trimmed = code.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setFeedback(null);
    try {
      const res = await creditsApi.redeem(trimmed);
      setFeedback({ ok: true, text: res.message });
      setCode('');
      void fetchUser();
    } catch (e: any) {
      setFeedback({ ok: false, text: e?.message || '兑换失败，请稍后重试' });
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[260] flex items-center justify-center p-4 animate-in fade-in duration-150"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-[400px] rounded-2xl p-6 animate-in zoom-in-95 duration-150"
        style={{ backgroundColor: '#1A1A1A', border: '1px solid #2D2D2D' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <div className="text-base font-semibold text-white">兑换码</div>
          <button onClick={onClose} aria-label="关闭" className="text-zinc-500 transition-colors hover:text-white">
            <X size={18} />
          </button>
        </div>
        <p className="mt-2 text-[12px] leading-[16px]" style={{ color: 'rgba(255,255,255,0.45)' }}>
          输入兑换码，积分或会员套餐即时到账
        </p>
        <input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void submit();
          }}
          placeholder="请输入兑换码"
          autoFocus
          spellCheck={false}
          className="mt-4 h-11 w-full rounded-xl px-4 text-sm text-white outline-none transition-colors placeholder:text-zinc-600 focus:border-[#4D4D4D]"
          style={{ backgroundColor: '#121212', border: '1px solid #363636' }}
        />
        {feedback && (
          <p className={`mt-3 text-[12px] leading-[16px] ${feedback.ok ? 'text-emerald-400' : 'text-[#E54545]'}`}>{feedback.text}</p>
        )}
        <button
          onClick={() => void submit()}
          disabled={busy || !code.trim()}
          className="mt-5 h-11 w-full rounded-xl text-sm font-medium text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-50 hover:opacity-90"
          style={{ backgroundColor: '#0092FF' }}
        >
          {busy ? '兑换中...' : '立即兑换'}
        </button>
      </div>
    </div>,
    document.body
  );
};

/** Banner：设计稿 164-40（1366x136 / 圆角 16）；
    外层 p-px 渐变描边（linear-gradient(-90deg) 渐变线向左：#4FA4B1 在右端 → #FFFFFF 在左端），
    内层背景图 cover + 水平镜像（设计稿 IMAGE fill 粉端朝右）+ 渐变遮罩
    （rgba(1,28,43) 左端实色 → 58% 处透明：左侧深青压暗、右端露出粉紫渐变）。
    流式适配：lg+ 固定 134 高、文案/倒计时垂直居中（等价设计稿 32/84 节奏）；窄屏纵排 auto 高，
    文案与倒计时字号/卡尺寸按 sm 分级缩小 */
const PromoBanner: React.FC<{ data: PromoBannerData }> = ({ data }) => {
  const t = useCountdown(data.endAt);
  return (
    <div
      className="rounded-2xl p-px"
      style={{ background: 'linear-gradient(-90deg, #4FA4B1 0%, #FFFFFF 100%)' }}
    >
      <div className="relative flex w-full flex-col justify-between gap-6 overflow-hidden rounded-[15px] px-6 py-6 sm:px-12 lg:h-[134px] lg:flex-row lg:items-center lg:gap-8 lg:py-0">
        {/* 背景图：运维平台可配（image_url），空值回落内置切图；scaleX(-1) 水平镜像对齐设计稿取向（粉端朝右、青端朝左） */}
        <img src={data.cover || '/icons/membership/banner-bg.png'} alt="" className="absolute inset-0 h-full w-full object-cover" style={{ transform: 'scaleX(-1)' }} />
        {/* 遮罩：90deg 渐变线向右，0% 实色在左端、58% 处渐透明 */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(1,28,43,1) 0%, rgba(1,28,43,0) 58%)' }} />
        {/* 文案：标题 32px Semibold、副标题 20px Light，字体 PingFang SC（Windows 回退雅黑；sm 以下分级缩小，间距 20px 对应设计稿 64→84） */}
        <div className="relative z-10 min-w-0">
          <div
            className="text-xl font-semibold leading-tight text-white sm:text-2xl lg:text-[32px] lg:leading-none"
            style={{ fontFamily: "'PingFang SC', 'Microsoft YaHei', sans-serif" }}
          >
            {data.title}
          </div>
          <div
            className="mt-2 text-sm font-light leading-tight text-white sm:text-base lg:mt-5 lg:text-xl lg:leading-none"
            style={{ fontFamily: "'PingFang SC', 'Microsoft YaHei', sans-serif" }}
          >
            {data.description}
          </div>
        </div>
        {/* 倒计时 4 卡：lg 80x96 / 间距 24；手机 64x80 / 间距 8 */}
        <div className="relative z-10 flex shrink-0 gap-2 sm:gap-4 lg:gap-6">
          <CountdownUnit value={t.d} unit="天" />
          <CountdownUnit value={t.h} unit="时" />
          <CountdownUnit value={t.m} unit="分" />
          <CountdownUnit value={t.s} unit="秒" />
        </div>
      </div>
    </div>
  );
};

/** 本地兜底方案：四档设计稿数据（158-25/161-244/161-245）。挂载后优先以 GET /api/plans 表驱动覆盖 */
/** 卡片主题：外框色 + 内卡渐变/描边 + 徽标配色 + 按钮名称段色（Figma 逐卡取值） */
interface PlanTheme {
  /** 外卡背景与描边色 */
  frame: string;
  /** 内卡渐变背景 */
  innerBg: string;
  /** 内卡描边（仅月度卡有） */
  innerBorder: string;
  badgeBg: string;
  badgeBorder: string;
  badgeText: string;
  /** 开通按钮「XXX」名称段颜色（月卡与基色同为 #141414，无独立覆盖） */
  buttonName: string;
}

interface PlanDef {
  key: 'monthly' | 'quarterly' | 'yearly' | 'yearlyPro';
  /** 内卡左上标题 */
  name: string;
  /** 开通按钮名称段（设计稿按钮文案「开通「月卡」会员」等，与卡片标题解耦） */
  shortName: string;
  /** 外框顶部条文案（12px Semibold 白居中） */
  stripText: string;
  /** 右上积分徽标 */
  pointsBadge: string;
  /** 价格数字（¥ 后） */
  price: string;
  /** 计费周期 */
  per: string;
  /** 原价（删除线，含「原价：」前缀，与周期间距 16px） */
  originalPrice: string;
  /** 积分盒文本 1（token 图标右侧） */
  genInfo1: string;
  /** 积分盒文本 2（第二行） */
  genInfo2: string;
  /** 权益列表 8 项 */
  benefits: string[];
  /** 卡片配色主题 */
  theme: PlanTheme;
}

/** 四卡配色主题（Figma 逐卡取值）：表驱动按 plan key 取用，本地 PLANS 与远端映射共用 */
const PLAN_THEMES: Record<string, PlanTheme> = {
  monthly: {
    frame: '#494949',
    innerBg: 'linear-gradient(27deg, #141414 0%, #2D2D2D 100%)',
    innerBorder: '1px solid #363636',
    badgeBg: 'rgba(126,126,126,0.05)',
    badgeBorder: 'rgba(126,126,126,0.25)',
    badgeText: '#D9D9D9',
    buttonName: '#141414',
  },
  quarterly: {
    frame: '#4FA4B1',
    innerBg: 'linear-gradient(207deg, #002A30 0%, #000E10 100%)',
    innerBorder: 'none',
    badgeBg: 'rgba(79,164,177,0.05)',
    badgeBorder: 'rgba(79,164,177,0.25)',
    badgeText: '#1FBED6',
    buttonName: '#4FA4B1',
  },
  yearly: {
    frame: '#ECAF10',
    innerBg: 'linear-gradient(207deg, #312300 0%, #150F00 100%)',
    innerBorder: 'none',
    badgeBg: 'rgba(236,175,16,0.05)',
    badgeBorder: 'rgba(236,175,16,0.25)',
    badgeText: '#FFB800',
    buttonName: '#ECAF10',
  },
  yearlyPro: {
    frame: '#FD475F',
    innerBg: 'linear-gradient(207deg, #3D0008 0%, #110000 100%)',
    innerBorder: 'none',
    badgeBg: 'rgba(253,71,95,0.05)',
    badgeBorder: 'rgba(253,71,95,0.25)',
    badgeText: '#FF0022',
    buttonName: '#FD475F',
  },
};

/** 四卡共用权益 8 项（设计稿 158-25 各卡权益一致） */
const PLAN_BENEFITS: string[] = [
  '全能图片模型随心使用',
  '解锁24张图一次生成权限，一次搞定诸多需求',
  '画布项目数量不限制',
  'Skill模板库数量不限制',
  '解锁所有图片、视频等操作AI功能',
  '专属AI生成通道，无需排队',
  '生成作品去除水印',
  'Seedance2.0/Minimax等诸多顶级模型使用',
];

const PLANS: PlanDef[] = [
  {
    key: 'monthly',
    name: '月卡',
    shortName: '月卡',
    stripText: '每月810积分，可用于图片、视频、音频等制作',
    pointsBadge: '每月810积分',
    price: '79',
    per: '/每月',
    originalPrice: '¥99',
    genInfo1: '810积分/月',
    genInfo2: '最多生成：81张2K图片 / 50张4K图片',
    benefits: PLAN_BENEFITS,
    theme: PLAN_THEMES.monthly,
  },
  {
    key: 'quarterly',
    name: '季卡',
    shortName: '季卡',
    stripText: '每季3050积分，可用于图片、视频、音频等制作',
    pointsBadge: '每季3050积分',
    price: '299',
    per: '/每季',
    originalPrice: '¥399',
    genInfo1: '3050积分/季',
    genInfo2: '最多生成：305张2K图片 / 190张4K图片',
    benefits: PLAN_BENEFITS,
    theme: PLAN_THEMES.quarterly,
  },
  {
    key: 'yearly',
    name: '年卡',
    shortName: '年卡',
    stripText: '赠送200积分，可用于图片、视频、音频等制作',
    pointsBadge: '赠送200积分',
    price: '599',
    per: '/每年',
    originalPrice: '原价：¥2099',
    genInfo1: '6190积分每年（1积分≈0.042元）',
    genInfo2: '最多生成：619张2K图片 / 386张4K图片',
    benefits: PLAN_BENEFITS,
    theme: PLAN_THEMES.yearly,
  },
  {
    key: 'yearlyPro',
    name: '年卡PRO',
    shortName: '年卡PRO',
    stripText: '赠送600积分，性价比首选，为创意而生',
    pointsBadge: '赠送600积分',
    price: '1399',
    per: '/每年',
    originalPrice: '原价：¥2099',
    genInfo1: '14590积分每年（1积分≈0.038元）',
    genInfo2: '最多生成：1459张2K图片 / 911张4K图片',
    benefits: PLAN_BENEFITS,
    theme: PLAN_THEMES.yearlyPro,
  },
];

/** 周期单位文案：month→月 / quarter→季 / year→年（未知值回落「月」） */
const PERIOD_UNIT: Record<string, string> = { month: '月', quarter: '季', year: '年' };

/** 开通按钮名称段短名（与卡片标题解耦） */
const PLAN_SHORT_NAME: Record<string, string> = {
  monthly: '月卡',
  quarterly: '季卡',
  yearly: '年卡',
  yearlyPro: '年卡PRO',
};

/** 价格分→元展示：整数省小数、非整数最多两位（7900→79 / 7990→79.9） */
const fmtYuan = (cents: number): string => {
  const yuan = cents / 100;
  return Number.isInteger(yuan) ? String(yuan) : String(Number(yuan.toFixed(2)));
};

/**
 * 套餐表驱动映射（P1）：GET /api/plans 行 → PlanDef[]，文案按公式生成——
 * 顶部条/徽标：bonus>0 走「赠送N积分」，否则「每X{N}积分」；
 * 积分盒行1：年卡含「1积分≈X元」单价注（价格/期总量），其余「N积分/每X」；
 * 积分盒行2：容量估算（2K=10积分/张、4K=16积分/张，与设计稿各卡数值一致）；
 * 未知 plan key 过滤、定价表留空返回空数组 → 调用方回落本地 PLANS（灰度语义）。
 */
const buildPlansFromRemote = (rows: PlanInfo[]): PlanDef[] =>
  rows
    .filter((row) => row.plan in PLAN_THEMES && row.plan in PLAN_SHORT_NAME)
    .map((row) => {
      const unit = PERIOD_UNIT[row.period ?? ''] ?? '月';
      const total = row.grantCredits;
      return {
        key: row.plan as PlanDef['key'],
        name: row.label,
        shortName: PLAN_SHORT_NAME[row.plan],
        stripText:
          row.bonusCredits > 0
            ? `赠送${row.bonusCredits}积分，可用于图片、视频、音频等制作`
            : `每${unit}${total}积分，可用于图片、视频、音频等制作`,
        pointsBadge: row.bonusCredits > 0 ? `赠送${row.bonusCredits}积分` : `每${unit}${total}积分`,
        price: fmtYuan(row.priceCents),
        per: `/每${unit}`,
        originalPrice: row.originalPriceCents != null ? `¥${fmtYuan(row.originalPriceCents)}` : '',
        genInfo1:
          row.period === 'year'
            ? `${total}积分每年（1积分≈${((row.priceCents / 100) / total).toFixed(3)}元）`
            : `${total}积分/${unit}`,
        genInfo2: `最多生成：${Math.floor(total / 10)}张2K图片 / ${Math.floor(total / 16)}张4K图片`,
        benefits: PLAN_BENEFITS,
        theme: PLAN_THEMES[row.plan],
      };
    });

/** 会员卡：外卡 312x604（36 顶部条 + 568 内卡）/ 圆角 24，配色由 plan.theme 驱动（四卡同构）。
    月度：外框 #494949、内卡 linear-gradient(27deg,#141414→#2D2D2D) + 1px #363636 描边；
    季度：#4FA4B1 / linear-gradient(207deg,#002A30→#000E10)；年卡：#ECAF10 / #312300→#150F00；
    年卡PRO：#FD475F / #3D0008→#110000（后三张内卡均无描边）。
    内卡纵向节奏（相对内卡）：标题 28 / 徽标 24 / 价格组 68 / 积分盒 108 / 按钮 208 / 权益 280 */
const MembershipCard: React.FC<{ plan: PlanDef }> = ({ plan }) => {
  const { theme } = plan;
  return (
    /* hover 动画与首页项目卡片（CanvasCard）一致：上浮 4px + 大阴影 */
    <div
      className="w-[312px] rounded-3xl transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-black/20"
      style={{ backgroundColor: theme.frame, border: `1px solid ${theme.frame}` }}
    >
      {/* 顶部条：36 高，文案 12px Semibold 白居中（top 12） */}
      <div className="flex h-9 items-start justify-center pt-3">
        <span className="text-xs font-semibold leading-none text-white">{plan.stripText}</span>
      </div>
      {/* 内卡：568 高 / 渐变底 / 圆角 24；仅月度卡有 1px #363636 描边 */}
      <div
        className="relative h-[568px] w-full rounded-3xl"
        style={{
          background: theme.innerBg,
          border: theme.innerBorder,
        }}
      >
        {/* 标题：16px Semibold 白 (24,28) */}
        <div className="absolute left-6 top-7 text-base font-semibold leading-none text-white">{plan.name}</div>
        {/* 积分徽标：右距 24 / top 24 / h-24 圆角 8；逐卡主题强调色（比外框更亮） */}
        <div
          className="absolute right-6 top-6 flex h-6 items-center rounded-lg px-2 text-xs leading-none"
          style={{ backgroundColor: theme.badgeBg, border: `1px solid ${theme.badgeBorder}`, color: theme.badgeText }}
        >
          {plan.pointsBadge}
        </div>
        {/* 价格组：(24,68) 基线对齐 —— ¥12px + 数字32px + 周期12px + 原价12px 删除线 45% 白（间距 16px） */}
        <div className="absolute left-6 top-[68px] flex h-8 items-baseline text-white">
          <span className="text-xs font-medium">¥</span>
          <span className="ml-0.5 text-[32px] font-semibold leading-none">{plan.price}</span>
          <span className="ml-1.5 text-xs font-medium">{plan.per}</span>
          <span className="ml-4 text-xs line-through" style={{ color: 'rgba(255,255,255,0.45)' }}>{plan.originalPrice}</span>
        </div>
        {/* 积分信息盒：(24,108) 264x72 / 白 10% / 圆角 8；行1 token+文本 白，行2 文本 65% 白 */}
        <div className="absolute left-6 top-[108px] h-[72px] w-[264px] rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
          <div className="absolute left-3 top-4 flex items-center">
            <img src="/icons/user-center/token-icon.svg" alt="" width={12} height={12} />
            <span className="ml-1 text-xs leading-none text-white">{plan.genInfo1}</span>
          </div>
          <div className="absolute left-3 top-11 text-xs leading-none" style={{ color: 'rgba(255,255,255,0.65)' }}>
            {plan.genInfo2}
          </div>
        </div>
        {/* 开通按钮：(24,208) 264x40 白底圆角 8；文字 14px Medium #141414，名称段 Semibold 主题色，尾缀「会员」；
            支付通道未开放（P3 待支付接入）：置灰 40% 保留点击 toast 反馈，去 hover 升亮 */}
        <button
          onClick={() => toast('开通通道即将开放，敬请期待')}
          className="absolute left-6 top-[208px] flex h-10 w-[264px] cursor-not-allowed items-center justify-center rounded-lg bg-white text-sm font-medium leading-none text-[#141414] opacity-40"
        >
          开通<span className="font-semibold" style={{ color: theme.buttonName }}>「{plan.shortName}」</span>会员
        </button>
        {/* 权益列表：(28,280) 256 宽，8 行 x 32 高；勾选 icon 12 + 文字 11px（图文间距 12） */}
        <div className="absolute left-7 top-[280px] w-64">
          {plan.benefits.map((benefit) => (
            <div key={benefit} className="flex h-8 items-center">
              <img src="/icons/membership/check-icon.svg" alt="" width={12} height={12} className="shrink-0" />
              <span className="ml-3 text-[11px] leading-8 text-white">{benefit}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/** 每日赠送句：仅 user.dailyGrantEnabled 开启时拼入 FAQ 首项答案 */
const FAQ_DAILY_SENTENCE = '每日赠送积分会在次日失效。';

/** 常见问题：6 项，文案按设计稿 164-39 定稿（含 v1.4 逐期清零规则表述）；积分有效期句已按 30 天滚动周期口径修正（与即梦一致，设计稿待同步） */
const FAQ_ITEMS: { q: string; a: string }[] = [
  {
    q: '积分有效期规则',
    a: '年付/季付会员每月发放的订阅积分自发放起30天内有效，到期清零（周期按会员开通日计算，例如3月15日开通，当期积分在4月14日清零，而非自然月1号）；月付会员的订阅积分在会员到期时同步清零；会员到期后订阅积分不再额外延长。',
  },
  {
    q: '会员/积分退款规则',
    a: '会员与积分属于虚拟数字商品，一经售出不支持退款，敬请谅解。',
  },
  {
    q: '关于会员续订',
    a: '目前暂不支持续订会员，只有会员过期后方可继续购买；会员过期前也可升级为更高档位会员。',
  },
  {
    q: '积分使用顺序',
    a: '使用优先级从前到后：订阅积分 → 团队订阅积分 → 充值积分。',
  },
  {
    q: '如何开发票',
    a: '前往账户中心-账户-发票管理，选择需要开票的订单及发票类型，扫描二维码填写发票信息即可开票，发票会在3个工作日内发送到邮箱。',
  },
  {
    q: '使用时遇到问题',
    a: '可点击下方"联系客服"，扫描二维码加入产品交流群，群内会有专业客服为您答疑解惑。',
  },
];

/** FAQ 手风琴项：问题 16px Medium lh24 + 答案 14px Light lh24（展开显示）；
    折叠按钮：chevron 16 包在 24x24 圆角 8 容器内，默认透明，鼠标指住问题行时
    显示 #2D2D2D 底且箭头提亮为纯白（设计稿 164-39 导出图标注的 hover 态，24x24 圆角 8）；
    箭头随展开旋转（收起朝下/展开朝上，与设计稿 ^ 方向一致）；
    每项后均有 0.5px 15% 白分隔线（含最后一项）；答案 mt-4 上距 */
const FaqItem: React.FC<{ item: { q: string; a: string }; open: boolean; onToggle: () => void }> = ({ item, open, onToggle }) => (
  <div className="border-b pb-8 pt-6 first:pt-0" style={{ borderColor: 'rgba(255,255,255,0.15)', borderBottomWidth: 0.5 }}>
    <button onClick={onToggle} className="group flex w-full items-start justify-between text-left">
      <div>
        <div className="text-base font-medium leading-6 text-white">{item.q}</div>
        {open && (
          <div className="mt-4 text-sm font-light leading-6" style={{ color: 'rgba(255,255,255,0.65)' }}>
            {item.a}
          </div>
        )}
      </div>
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg transition-colors duration-200 group-hover:bg-[#2D2D2D]">
        <ChevronDown
          size={16}
          className="text-white opacity-65 transition-all duration-200 group-hover:opacity-100"
          style={{ transform: open ? 'rotate(180deg)' : 'none' }}
        />
      </span>
    </button>
  </div>
);

/** 受众类型：个人创作（默认，对应现有 4 卡）/ 团队协作 */
export type Audience = 'personal' | 'team';

/** 个人创作/团队协作切换器：设计稿 158-471「个人/团队」——
    轨道 216x48 圆角 24 白 5%；选中滑块 104x40 圆角 24 白 10%
    （左态 x=4 / 右态 x=108，200ms 平滑滑动）；选项文字 14px：
    选中 Semibold 白 / 未选 Medium 白 65%。
    设计稿仅有「个人创作」选中态画板，团队页未出稿，切至团队态由父组件渲染占位提示 */
const AudienceSwitcher: React.FC<{
  value: Audience;
  onChange: (v: Audience) => void;
  className?: string;
}> = ({ value, onChange, className }) => (
  <div
    className={`relative mx-auto h-12 w-[216px] rounded-3xl ${className ?? ''}`}
    style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}
  >
    {/* 滑块：left-4 基准，右态位移 104px（对应设计稿 x=4 / x=108 两态） */}
    <div
      className="absolute left-1 top-1 h-10 w-[104px] rounded-3xl transition-transform duration-200 ease-out"
      style={{
        backgroundColor: 'rgba(255,255,255,0.1)',
        transform: value === 'personal' ? 'translateX(0px)' : 'translateX(104px)',
      }}
    />
    {/* 选项区：p-1 内两选项各 104 宽，与滑块左右两态位置一一对应 */}
    <div className="relative flex h-full w-full p-1">
      <button
        type="button"
        onClick={() => onChange('personal')}
        className={`h-10 w-[104px] text-sm leading-none transition-colors ${
          value === 'personal' ? 'font-semibold text-white' : 'font-medium'
        }`}
        style={value === 'personal' ? undefined : { color: 'rgba(255,255,255,0.65)' }}
      >
        个人创作
      </button>
      <button
        type="button"
        onClick={() => onChange('team')}
        className={`h-10 w-[104px] text-sm leading-none transition-colors ${
          value === 'team' ? 'font-semibold text-white' : 'font-medium'
        }`}
        style={value === 'team' ? undefined : { color: 'rgba(255,255,255,0.65)' }}
      >
        团队协作
      </button>
    </div>
  </div>
);

/**
 * 订阅会员弹窗（全屏页）：createPortal 到 body / z-[250] / #121212 整页 / 内容列 1366 居中。
 * ESC 关闭 + body 滚动锁定；FAQ 默认全部折叠（点击展开，-1 表示无展开项）。
 */
export const MembershipModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { user } = useAuthStore();
  const tier = resolveTier(user?.membershipTier);
  const [openFaq, setOpenFaq] = useState(-1);
  // 受众切换：个人创作显示现有 4 卡 + FAQ；团队协作暂无设计稿，显示占位提示
  const [audience, setAudience] = useState<Audience>('personal');
  // 套餐表驱动（P1）：默认本地设计稿 PLANS，挂载即拉 /api/plans 覆盖；失败或定价表留空保持本地
  const [plans, setPlans] = useState<PlanDef[]>(PLANS);
  // Banner 运营化（P2）：默认设计稿兜底文案，挂载即拉 GET /api/banners?position=membership 覆盖；
  // 倒计时截止取运维平台轮播图「结束时间」，留空回落 BETA_END_AT
  const [banner, setBanner] = useState<PromoBannerData>(DEFAULT_BANNER);
  // 会员页配置运营化（P2）：挂载即拉 GET /api/membership-page-config，
  // config.heroTitle / heroSubtitle 覆盖主副标题（逐字段覆盖，缺失字段保持本地兜底）；
  // faqs 非空时整体替换本地 FAQ_ITEMS（远程 FAQ 由运维平台整条管理，不再拼本地「每日赠送」句）
  const [hero, setHero] = useState({
    title: '订阅 Bong 会员，让创作更快一步',
    subtitle: '选择合适您的会员方案，解锁更高积分额度和全部创作能力',
  });
  const [remoteFaqs, setRemoteFaqs] = useState<{ q: string; a: string }[] | null>(null);
  // 兑换码弹窗（P2 运维平台「兑换码」）：顶栏「兑换码」入口
  const [redeemOpen, setRedeemOpen] = useState(false);
  // FAQ 首项按每日赠送开关拼句（仅本地兜底 FAQ）：关闭每日赠送的部署不出现「每日赠送积分」表述
  const faqItems = useMemo(() => {
    const base = remoteFaqs ?? FAQ_ITEMS;
    return base.map((item, index) =>
      remoteFaqs === null && index === 0 && user?.dailyGrantEnabled
        ? { ...item, a: `${FAQ_DAILY_SENTENCE}${item.a}` }
        : item
    );
  }, [user?.dailyGrantEnabled, remoteFaqs]);

  // 套餐拉取：组件实例级竞态防护（disposed），覆盖失败静默
  useEffect(() => {
    let disposed = false;
    creditsApi
      .getPlans()
      .then((rows) => {
        if (disposed) return;
        const mapped = buildPlansFromRemote(rows);
        if (mapped.length > 0) setPlans(mapped);
      })
      .catch(() => {
        // 拉取失败静默回落本地 PLANS（设计稿文案完整可用）
      });
    return () => {
      disposed = true;
    };
  }, []);

  // 会员页 Banner 拉取：运维平台「运营管理-轮播图」（位置=会员页）取第一条上线记录覆盖兜底；
  // 失败或空数据静默保持 DEFAULT_BANNER（组件实例级竞态防护与套餐拉取一致）
  useEffect(() => {
    let disposed = false;
    fetch('/api/banners?position=membership')
      .then((res) => (res.ok ? res.json() : null))
      .then((rows) => {
        if (disposed || !Array.isArray(rows) || rows.length === 0) return;
        const row = rows[0];
        const endAt = row.endTime ? new Date(row.endTime).getTime() : NaN;
        setBanner({
          title: row.title || DEFAULT_BANNER.title,
          description: row.description || DEFAULT_BANNER.description,
          cover: row.cover || '',
          endAt: Number.isFinite(endAt) ? endAt : BETA_END_AT,
        });
      })
      .catch(() => {
        // 拉取失败静默回落设计稿兜底文案
      });
    return () => {
      disposed = true;
    };
  }, []);

  // 会员页配置拉取（运维平台「会员页/FAQ」）：heroTitle/heroSubtitle 非空逐字段覆盖主副标题，
  // faqs 非空整体替换本地 FAQ；失败/空配置/JSON 损坏静默回落设计稿兜底（竞态防护同上）
  useEffect(() => {
    let disposed = false;
    fetch('/api/membership-page-config')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (disposed || !data) return;
        const cfg = (data.config ?? {}) as Record<string, unknown>;
        const title = typeof cfg.heroTitle === 'string' && cfg.heroTitle.trim() ? cfg.heroTitle.trim() : '';
        const subtitle = typeof cfg.heroSubtitle === 'string' && cfg.heroSubtitle.trim() ? cfg.heroSubtitle.trim() : '';
        if (title || subtitle) {
          setHero((prev) => ({ title: title || prev.title, subtitle: subtitle || prev.subtitle }));
        }
        if (Array.isArray(data.faqs) && data.faqs.length > 0) {
          const mapped = (data.faqs as { question?: unknown; answer?: unknown }[])
            .filter((f) => typeof f.question === 'string' && typeof f.answer === 'string')
            .map((f) => ({ q: f.question as string, a: f.answer as string }));
          if (mapped.length > 0) setRemoteFaqs(mapped);
        }
      })
      .catch(() => {
        // 拉取失败静默回落本地兜底（标题副标题 + FAQ_ITEMS）
      });
    return () => {
      disposed = true;
    };
  }, []);

  // ESC 关闭 + body 滚动锁定
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  // v1.13 审计补齐：本期订阅积分到期倒计时 + 下期到账预告（个人页会员卡与 FAQ 之间展示，
  // 仅订阅用户有 currentPeriodExpiresAt 时渲染；下期预告仅 nextPeriodCredits > 0 时展示）
  const expiresAt = user?.currentPeriodExpiresAt ? new Date(user.currentPeriodExpiresAt) : null;
  const daysLeft = expiresAt ? Math.ceil((expiresAt.getTime() - Date.now()) / 86400000) : null;
  const expireLabel = expiresAt ? expiresAt.toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric' }) : '';
  const nextPeriodCredits = user?.nextPeriodCredits ?? 0;

  return createPortal(
    <div className="fixed inset-0 z-[250] overflow-y-auto animate-in fade-in duration-150" style={{ backgroundColor: '#121212' }}>
      <ModalTopBar tier={tier} onBack={onClose} onOpenRedeem={() => setRedeemOpen(true)} />
      <div className="mx-auto w-full max-w-[1366px] px-4 pb-20 sm:px-6 min-[1440px]:px-0">
        {/* Banner：紧贴顶栏（设计稿 y=112 即顶栏底）；文案/背景/倒计时截止由运维平台接口驱动 */}
        <PromoBanner data={banner} />

        {/* 渐变主标题区：设计稿 251-41 —— 主标题 Semibold 34px/34px（行高 1:1）居中，
            渐变 90deg 蓝0%→白24%-31%→红75%→深红100%；副标题位于 y=50（间距16px）14px/14px Light 白65%。
            inline-block 使渐变作用于文本宽度（块级铺满整行会让文字落入中段白色区间）。
            行距保持 1:1 与设计稿一致；g 降部溢出行盒的裁切用 pb-2 解决：
            background-clip:text 的背景绘制到 padding 盒，溢出墨迹落入 padding 区仍被渐变着色；
            副标题 mt-2 已扣除该 padding（8+8=16），维持设计稿 16px 间距 */}
        <div className="mt-16 text-center">
          <div
            className="inline-block pb-2 text-2xl font-semibold leading-none sm:text-3xl lg:text-[34px]"
            style={{
              backgroundImage: 'linear-gradient(90deg, rgba(14,161,240,1) 0%, rgba(255,255,255,1) 24%, rgba(255,255,255,1) 31%, rgba(253,71,95,1) 75%, rgba(204,0,27,1) 100%)',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {hero.title}
          </div>
          <div className="mt-2 text-sm font-light leading-none" style={{ color: 'rgba(255,255,255,0.65)' }}>
            {hero.subtitle}
          </div>
        </div>

        {/* 个人创作/团队协作切换器：设计稿 158-471（216x48），上距主标题 48 / 与卡片间距 32 */}
        <AudienceSwitcher className="mt-12" value={audience} onChange={setAudience} />

        {audience === 'personal' ? (
          <>
            {/* 会员卡 x4：+32 间距（设计稿切换器底 472 → 卡顶 504），单行 4x312+3x16=1296 居中（间距 16）；<md 纵向堆叠 / 窄屏自动换行 */}
            <div className="mt-8 flex flex-col items-center justify-center gap-4 px-4 md:flex-row md:flex-wrap">
              {plans.map((plan) => (
                <MembershipCard key={plan.key} plan={plan} />
              ))}
            </div>

            {/* 本期积分到期倒计时 + 下期到账预告：贴会员卡 +24 间距，居中横条（设计稿未含该区块，
                沿用积分信息盒视觉语言：白 6% 底圆角 16 / 14px 85% 白，预告强调色 #8BCDFF 与昵称一致） */}
            {expiresAt && (
              <div className="mt-6 flex justify-center px-4">
                <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 rounded-2xl px-4 py-3 text-sm leading-none" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}>
                  <img src="/icons/user-center/token-icon.svg" alt="" width={12} height={12} />
                  <span style={{ color: 'rgba(255,255,255,0.85)' }}>本期积分将于 {expireLabel} 到期清零</span>
                  {daysLeft != null && daysLeft >= 0 && <span style={{ color: 'rgba(255,255,255,0.45)' }}>（剩 {daysLeft} 天）</span>}
                  {nextPeriodCredits > 0 && (
                    <span className="font-medium" style={{ color: '#8BCDFF' }}>下期将到账 {nextPeriodCredits} 积分</span>
                  )}
                </div>
              </div>
            )}

            {/* 常见问题：+64 间距，1024 为上限随窗口收缩；标题 24px Semibold 居中，下方 72px 首项 */}
            <div className="mx-auto mt-16 w-full max-w-[1024px]">
              <div className="text-center text-2xl font-semibold leading-6 text-white">常见问题</div>
              <div className="mt-[72px]">
                {faqItems.map((item, index) => (
                  <FaqItem
                    key={item.q}
                    item={item}
                    open={openFaq === index}
                    onToggle={() => setOpenFaq(openFaq === index ? -1 : index)}
                  />
                ))}
              </div>
            </div>
          </>
        ) : (
          // 团队协作占位：设计稿暂未提供团队页，按项目「敬请期待」惯例占位
          <div className="py-24 text-center text-sm font-medium" style={{ color: 'rgba(255,255,255,0.65)' }}>
            团队协作功能即将上线，敬请期待
          </div>
        )}
      </div>

      {/* 兑换码弹窗（P2 运维平台「兑换码」核销入口） */}
      {redeemOpen && <RedeemDialog onClose={() => setRedeemOpen(false)} />}
    </div>,
    document.body
  );
};
